import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ResumableGame, ResumeOpponents } from '~/session/resume.js';
import { findResumable } from '~/session/resumeLookup.js';
import { useAuthStore, useDevicePlayerId, useOwnFace } from '~/store/authStore.js';
import { Avatar, inwardFacing } from '~/ui/Avatar.js';
import { seatFace } from '~/session/faceOrder.js';
import { MenuTile } from './MenuTile.js';
import { useLatestRequest } from './useLatestRequest.js';

/**
 * The home screen's way back into a game: a local one left unfinished, or an
 * online table still being played.
 *
 * The hook finds the game and the door draws it. They are apart so a home
 * layout can place the door where its stack puts the next step, and turn its
 * own Play door white while one is showing: one lemon door per screen.
 */

export type { ResumableGame } from '~/session/resume.js';

/**
 * How long the home waits before drawing its doors without a Resume. A healthy
 * IndexedDB answers within a few frames, and Rails within a couple of hundred
 * milliseconds. The Cross invitation's device read settles on the same
 * deadline, so the home has one wait.
 */
export const RESUME_DEADLINE_MS = 400;

/**
 * How often an online door asks again while it is on screen. The table goes on
 * without the player, and the door shows the score as it moves (Mitch,
 * 2026-09-30).
 */
const LIVE_POLL_MS = 5_000;

/**
 * How often the home asks again after an online door has gone. Rails answers
 * "no game" when the game service misses its 1 s deadline, so a door can go
 * for a game that is still being played; this is how it comes back.
 */
const DROPPED_POLL_MS = 15_000;

/**
 * The lookup as it stands. `settled` turns true on the first answer or at the
 * deadline, whichever comes first. `answered` turns true only on an answer.
 */
export interface ResumeCheck {
  readonly resume: ResumableGame | null;
  readonly settled: boolean;
  readonly answered: boolean;
}

/**
 * The game this player can go back to, and whether the answer is in yet.
 *
 * `findResumable` decides which game: the newest one started or joined, local
 * or online. A finished game never qualifies.
 *
 * `settled` is what keeps the stack from changing color under the player. The
 * answer comes back a few frames after the home first paints. A home that drew
 * Play lemon until then faded it to white in front of the player whenever a
 * game was waiting. So hold the doors until `settled`, then draw them once.
 *
 * A lookup that never answers settles anyway, after `RESUME_DEADLINE_MS`, with
 * no game. A blocked `indexedDB.open` or a slow network can hang without
 * failing, and a home held for that would have no Play door at all. An answer
 * that lands after the deadline still shows its door. So `settled` is not
 * proof that no game is waiting: a caller that must never show beside a game
 * in progress waits for `answered` instead.
 *
 * The lookup waits for the session to be restored. The stored token is in the
 * auth store from the first frame, but the API client is handed it only once
 * `restore` runs, so asking sooner went out unsigned and came back 401. A
 * stack already drawn is not hidden again when the session arrives; a game
 * found then still shows its door.
 *
 * The player is the one the device holds a session for, known before `/me`
 * answers (`useDevicePlayerId`). A launch with no network never gets an answer,
 * and its local game is still this player's to resume.
 *
 * While an online game is on the door, it is asked for again every
 * `LIVE_POLL_MS`, skipped while the page is hidden. A game that ends in the
 * meantime takes its door with it, on the second empty answer in a row: one
 * can be the game service missing Rails' 1 s deadline. A server that cannot be
 * reached leaves the door as it is. After an online door goes, the home keeps
 * asking every `DROPPED_POLL_MS` while it is open, because two slow answers in
 * a row look exactly like a finished game. A caller that draws no door passes
 * `poll: false` and asks once.
 */
export function useResumeCheck({
  poll: polls = true,
}: { readonly poll?: boolean } = {}): ResumeCheck {
  const playerId = useDevicePlayerId();
  const online = useAuthStore((state) => state.token !== null);
  const ready = useAuthStore((state) => state.ready);
  // The door's own head wears the face the player chose, and follows it when
  // `/me` answers after the first look.
  const you = useOwnFace();
  const request = useLatestRequest();
  const poll = useLatestRequest();
  const [check, setCheck] = useState<ResumeCheck>({
    resume: null,
    settled: false,
    answered: false,
  });
  // An online door has been shown for this player, so the home keeps asking.
  const [watching, setWatching] = useState(false);
  const shown = useRef<ResumableGame | null>(null);
  useEffect(() => {
    shown.current = check.resume;
  }, [check.resume]);

  useEffect(() => {
    const isCurrent = request.begin();
    // Another player's game is not this one's to offer.
    setCheck((current) =>
      current.resume === null && !current.answered
        ? current
        : { ...current, resume: null, answered: false },
    );
    setWatching(false);
    const deadline = setTimeout(() => {
      if (isCurrent())
        setCheck((current) => (current.settled ? current : { ...current, settled: true }));
    }, RESUME_DEADLINE_MS);
    if (ready) {
      void findResumable({ playerId, online, you }).then((resume) => {
        clearTimeout(deadline);
        if (!isCurrent()) return;
        setCheck({ resume, settled: true, answered: true });
        if (resume?.kind === 'online' && polls) setWatching(true);
      });
    }
    return () => {
      clearTimeout(deadline);
      request.retire();
    };
  }, [request, playerId, online, ready, polls, you]);

  const live = check.resume?.kind === 'online';
  useEffect(() => {
    if (!watching) return;
    let missed = false;
    const every = live ? LIVE_POLL_MS : DROPPED_POLL_MS;
    const timer = setInterval(() => {
      if (globalThis.document?.visibilityState === 'hidden') return;
      const isCurrent = poll.begin();
      void findResumable({ playerId, online, showing: shown.current, you }).then((resume) => {
        if (!isCurrent()) return;
        if (resume === null && !missed) {
          missed = true;
          return;
        }
        missed = false;
        setCheck({ resume, settled: true, answered: true });
      });
    }, every);
    return () => {
      clearInterval(timer);
      poll.retire();
    };
  }, [poll, watching, live, playerId, online, you]);

  return check;
}

/**
 * The Resume door, drawn off the `lobby-home` sketch's `poster-live` exhibit:
 * the lemon door at the poster's `feature` size, led by the score, with the
 * title at display size and one line on the game underneath. The heads wear
 * ink alone, because teal reads 1.10:1 on lemon.
 *
 * The score is drawn for the eye and said once in words for a screen reader,
 * so the door is not announced as two bare numbers.
 */
export function ResumeDoor({ resume }: { readonly resume: ResumableGame }): React.JSX.Element {
  const { t } = useTranslation();
  const { summary } = resume;
  const scores = summary.sides
    .map((side) => String(side.score))
    .join(t('home.resumePastGame.scoreJoin'));
  return (
    <MenuTile
      size="feature"
      variant="primary"
      to={resume.href}
      lead={
        <span className="flex flex-none flex-col gap-1 tablet:flex-row tablet:gap-4">
          {summary.sides.map((side, index) => (
            <span key={side.team} className="flex items-center gap-2" aria-hidden="true">
              {/* The sketch's two head sizes, stacked on a phone and side by
                  side from tablet up. Two drawings rather than one rescaled,
                  because a head's plate is geometry drawn at its size. */}
              <span className="tablet:hidden">
                <Avatar {...seatFace(side)} side="rows" size="chip" outline="plain" still />
              </span>
              <span className="hidden tablet:block">
                <Avatar
                  {...seatFace(side)}
                  side="rows"
                  facing={inwardFacing(index, summary.sides.length)}
                  size="panel"
                  outline="plain"
                  still
                />
              </span>
              <span className="text-xl leading-none font-extrabold tablet:text-2xl">
                {side.score}
              </span>
            </span>
          ))}
          <span className="sr-only">{t('home.resumePastGame.score', { scores })}</span>
        </span>
      }
      label={t('home.resumePastGame.title')}
      hint={t('home.resumePastGame.details', {
        minutes: summary.minutes,
        players: summary.players,
        opponents: opponentsLine(t, resume.opponents),
      })}
    />
  );
}

/** The last words of the door's line: who the player is up against. */
function opponentsLine(
  t: ReturnType<typeof useTranslation>['t'],
  opponents: ResumeOpponents,
): string {
  switch (opponents.kind) {
    case 'pass':
      return t('home.resumePastGame.passPlay');
    case 'online':
      return t('home.resumePastGame.online');
    default:
      return t('home.resumePastGame.bots', { difficulty: t(`setup.${opponents.difficulty}`) });
  }
}
